import { faEnvelope, faUnlockAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
} from "@themesberg/react-bootstrap";
import { useFormik } from "formik";
import { AnimatePresence, motion } from "framer-motion";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { FaArrowLeft } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import Logo from "../../assets/img/logo/logo.svg";
import { forgotPassword, sign } from "../../services/apis";
import { isUserLogged } from "../../utils/helpers/function";

const Login = () => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loader, setLoader] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const navigate = useNavigate();

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleForgotPasswordClick = () => {
    setIsForgotPassword(true);
    loginForm.resetForm();
  };

  const handleBackToLoginClick = () => {
    setIsForgotPassword(false);
    forgotPasswordForm.resetForm();
  };

  const loginForm = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email("Enter a valid email address")
        .required("Email cannot be empty")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Enter a valid email address"
        ),
      password: Yup.string()
        .required("Password cannot be empty.")
        .min(8, "Password must be at least 8 characters long.")
        .matches(
          /[A-Z]/,
          "Password must contain at least one uppercase letter."
        )
        .matches(
          /[a-z]/,
          "Password must contain at least one lowercase letter."
        )
        .matches(/[0-9]/, "Password must contain at least one number.")
        .matches(
          /[!@#$%^&*(),.?":{}|<>]/,
          "Password must contain at least one special character."
        ),
    }),
    onSubmit: async (values) => {
      setLoader(true);

      try {
        const response = await sign(values);

        if (response?.statuscode === 1) {
          if (response?.token) {
            const accessToken = response?.token;
            const tokenData = isUserLogged("login", accessToken);
            Cookies.set("userRole", tokenData?.role, { expires: 7 });

            if (tokenData?.role === "super admin") {
              navigate("/super-admin/dashboard");
            } else if (tokenData?.role === "admin") {
              navigate("/admin/dashboard");
            } else {
              Cookies.remove("zk-vg");
              toast.dismiss();
              toast.warning("Unrecognized role. Please contact support.");
            }
          } else {
            toast.dismiss();
            toast.warning(response?.message || "Unable To Login");
          }
        } else {
          if (response?.errors?.password) {
            loginForm.setFieldError("password", response.errors.password);
          } else {
            toast.dismiss();
            toast.warning(
              response?.message || "Invalid credentials. Please try again."
            );
          }
        }
      } catch (error) {
        console.log(error);
        const errorMessage =
          error.response?.data?.message ||
          "An error occurred. Please try again.";
        toast.dismiss();
        toast.error(errorMessage);
      } finally {
        setLoader(false);
      }
    },
  });

  const forgotPasswordForm = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email("Enter a valid email address")
        .required("Email cannot be empty")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Enter a valid email address"
        ),
    }),
    onSubmit: async (values) => {
      setLoader(true);
      try {
        const response = await forgotPassword(values);

        if (
          response?.statuscode === 1 &&
          response?.message != "Check your email for verification."
        ) {
          console.log("res1", response);
          toast.dismiss();
          toast.success(response.message);
        } else {
          console.log("res2", response);
          toast.dismiss();
          toast.warning(
            response?.message || "An error occurred. Please try again."
          );
        }
      } catch (error) {
        console.log(error);
        const errorMessage =
          error.response?.data?.message ||
          "An error occurred. Please try again.";
        toast.dismiss();
        toast.error(errorMessage);
      } finally {
        setLoader(false);
      }
    },
  });

  useEffect(() => {
    Cookies.remove("zk-vg");
    Cookies.remove("userRole");
  }, []);

  return (
    <main className="d-flex align-items-center justify-content-center min-vh-100">
      <section className="d-flex align-items-center justify-content-center">
        <ToastContainer />
        <Container>
          {/* <Row
            className="justify-content-center form-bg-image"
            style={{ backgroundImage: `url(${BgImage})` }}
          > */}
          <div className="d-flex align-items-center justify-content-center mb-4">
            <img src={Logo} style={{ width: 180, height: 45 }} />
          </div>
          <Col
            xs={12}
            className="d-flex align-items-center justify-content-center"
          >
            <AnimatePresence>
              <div
                style={{ transition: "all 0.4s linear" }}
                className="bg-white shadow border gap-5 rounded-xl overflow-hidden border-light p-4 p-lg-4 w-100 fmxw-450 d-flex"
              >
                <motion.div
                  initial={{ translateX: "0px" }}
                  animate={{ translateX: isForgotPassword ? "-450px" : "0px" }}
                  transition={{ ease: "linear", duration: 0.2 }}
                  className="d-flex gap-5 form-container"
                >
                  <Form
                    style={{ width: 400 }}
                    onSubmit={loginForm.handleSubmit}
                  >
                    <Form.Group id="email" className="mb-4">
                      <Form.Label>Email Address</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faEnvelope} />
                        </InputGroup.Text>
                        <Form.Control
                          autoFocus
                          // required
                          type="text"
                          placeholder="Enter Email"
                          {...loginForm.getFieldProps("email")}
                        />
                      </InputGroup>
                      {loginForm.touched.email && loginForm.errors.email && (
                        <div className="text-danger">
                          {loginForm.errors.email}
                        </div>
                      )}
                    </Form.Group>

                    <Form.Group id="password" className="mb-4">
                      <Form.Label>Password</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faUnlockAlt} />
                        </InputGroup.Text>
                        <Form.Control
                          // required
                          type={passwordVisible ? "text" : "password"}
                          placeholder="Enter Password"
                          {...loginForm.getFieldProps("password")}
                        />
                        <InputGroup.Text
                          onClick={togglePasswordVisibility}
                          style={{ cursor: "pointer" }}
                        >
                          {passwordVisible ? (
                            <FaEye style={{ width: 25, height: 15 }} />
                          ) : (
                            <FaEyeSlash style={{ width: 25, height: 15 }} />
                          )}
                        </InputGroup.Text>
                      </InputGroup>
                      {loginForm.touched.password &&
                        loginForm.errors.password && (
                          <div className="text-danger">
                            {loginForm.errors.password}
                          </div>
                        )}
                    </Form.Group>

                    <div className="d-flex justify-content-end align-items-center mb-4">
                      <Card.Link
                        className="small fw-bold text-end"
                        onClick={handleForgotPasswordClick}
                      >
                        Forgot Password?
                      </Card.Link>
                    </div>

                    <Button
                      style={{ color: "white" }}
                      type="submit"
                      className="w-100"
                      disabled={loader}
                    >
                      {loader ? "Logging in..." : "Login"}
                    </Button>
                  </Form>

                  {isForgotPassword && (
                    <div
                      className="form-container"
                      initial={{ translateX: "" }}
                      animate={{ translateX: isForgotPassword ? 0 : "" }}
                      transition={{ ease: "linear", duration: 0.2 }}
                    >
                      <div className="d-flex justify-content-start align-items-center mb-4">
                        <Card.Link
                          className="small fw-bold text-end align-items-center display-6 d-flex"
                          onClick={handleBackToLoginClick}
                        >
                          <FaArrowLeft
                            className="rounded-circle"
                            style={{
                              marginRight: 6,
                              padding: 7,
                              width: 30,
                              height: 30,
                              backgroundColor: "#ecffdb",
                            }}
                          />{" "}
                          Back to Login
                        </Card.Link>
                      </div>
                      <Form
                        style={{ width: 400 }}
                        onSubmit={forgotPasswordForm.handleSubmit}
                      >
                        <Form.Group id="email" className="mb-4">
                          <Form.Label>Email Address</Form.Label>
                          <InputGroup>
                            <InputGroup.Text>
                              <FontAwesomeIcon icon={faEnvelope} />
                            </InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Enter email"
                              {...forgotPasswordForm.getFieldProps("email")}
                            />
                          </InputGroup>
                          {forgotPasswordForm.touched.email &&
                            forgotPasswordForm.errors.email && (
                              <div className="text-danger">
                                {forgotPasswordForm.errors.email}
                              </div>
                            )}
                        </Form.Group>

                        <Button
                          style={{ color: "white" }}
                          type="submit"
                          className="w-100"
                          disabled={loader}
                        >
                          {loader ? "Sending..." : "Send password reset email"}
                        </Button>
                      </Form>
                    </div>
                  )}
                </motion.div>
              </div>
            </AnimatePresence>
          </Col>
          {/* </Row> */}
        </Container>
      </section>
    </main>
  );
};

export default Login;
